import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { ReactNode } from 'react';
import Scrollbar from 'src/components/scrollbar';
import { TablePaginationCustom, useTable } from 'src/components/table';
import { GetTemplateDtoTemplateTypeEnum } from 'woofwoof-api';
import Image from '../../../../components/image';
import {
  categoryTranslations,
  templateTypeTranslations,
} from '../context/TemplatesContext';
import { useTemplatesContext } from '../hooks/useTemplatesContext';
import { translateKey } from 'src/locales/translate';


interface Row {
  id: string;
  name: string;
  description?: string;
  category: string;
  templateType: GetTemplateDtoTemplateTypeEnum;
  updatedAt: string;
  isShared: boolean;
}

interface Column {
  id: keyof Row;
  label: string;
  minWidth?: number;
  align?: 'left' | 'center';
  render?: (row: Row) => ReactNode;
}

export default function TemplatesTable() {
  const { templatesData, handleOpenModal, isTemplatesDataLoading } =
    useTemplatesContext();

  const handleOnRowClick = (templateId: string) => {
    handleOpenModal(templateId);
  };

  const tableData: Row[] =
    templatesData?.map((template) => ({
      id: template.id,
      name: template.name,
      description: template.description,
      category: template.category,
      templateType: template.templateType,
      updatedAt: template.updatedAt,
      isShared: template.isShared,
    })) || [];

  const COLUMNS: Column[] = [
    {
      id: 'name',
      label: 'Nazwa',
      render: (value: Row) => value.name,
    },
    {
      id: 'description',
      label: translateKey("search.ui.aboutTab"),
      render: (value: Row) => value.description,
    },
    {
      id: 'category',
      label: translateKey("search.ui.category"),
      render: (value: Row) =>
        categoryTranslations[value.category] ||
        value.category
          .toString()
          .split('_')
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
    },
    {
      id: 'templateType',
      label: 'Typ',
      render: (value: Row) =>
        templateTypeTranslations[value.templateType] ||
        value.templateType
          .toString()
          .split('_')
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
    },
    {
      id: 'updatedAt',
      label: 'Ostatnia aktualizacja',
      render: (value: Row) =>
        format(new Date(value.updatedAt), 'dd/MM/yyyy H:mm'),
    },
    {
      id: 'isShared',
      label: translateKey("visit.tableStatus"),
      render: (value: Row) =>
        value.isShared ? (
          <Chip label={translateKey("hardcoded.udostepniony")} variant="soft" color="success" />
        ) : (
          <Chip label="Prywatny" variant="soft" color="warning" />
        ),
    },
  ];
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: 25,
  });

  const TableHeader = (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          {COLUMNS.map((column) => (
            <TableCell
              key={column.id}
              align={column.align}
              style={{ minWidth: column.minWidth }}
            >
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    </Table>
  );

  const FullTable = (
    <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}>
      <Scrollbar>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, rowIndex) => (
                <TableRow
                  hover
                  key={rowIndex}
                  onClick={() => handleOnRowClick(row.id)}
                >
                  {COLUMNS.map((column, index) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.render ? column.render(row) : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </Box>
  );

  return (
    <>
      <TableContainer
        sx={{ height: 1, display: 'flex', flexDirection: 'column' }}
      >
        {tableData.length > 0 ? (
          FullTable
        ) : !isTemplatesDataLoading ? (
          <>
            {TableHeader}
            <Stack
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              flex="1"
              gap={2}
            >
              <Image
                sx={{ width: '200px' }}
                alt="empty waiting room"
                src="/assets/images/empty_table.svg"
              />
              <Typography>{translateKey("hardcoded.brak_szablonow")}</Typography>
            </Stack>
          </>
        ) : null}
      </TableContainer>

      <TablePaginationCustom
        count={tableData.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </>
  );
}
